/*
 * FORMULAR-VORBEFUELLUNG FUER BUEHNEN-KACHELN (Rafael, 05.10.2026 19:38Z, Freigabe „a"
 * auf e-gewinnspiele-fuellen; Bauauftrag privat/gewinnspiele/bauauftrag-vorbefuellung.md).
 *
 * Eine Browser-Kachel kann `inhalt.ausfuellen` tragen:
 *   { profil: "rafael", felder: [{ selektor: "#vorname", wert: "{vorname}" },
 *                                { selektor: "input[name=agb]", haken: true }] }
 * Das Panel traegt die Werte bei JEDEM Laden der Seite wieder ein. Die Werte stehen NICHT
 * in der Buehne, sondern in einer Profildatei (Server, admin-only); `{feld}` wird daraus ersetzt.
 *
 * Harte Grenzen, alle im erzeugten Skript (baueFuellSkript):
 *   - nur LEERE Felder werden gefuellt — was ein Mensch geaendert hat, bleibt stehen;
 *   - ein Haken wird nur geklickt, wenn er noch nicht gesetzt ist;
 *   - NIE abschicken: kein submit(), kein Klick auf Knoepfe, Cookie-Dialoge unberuehrt;
 *   - jedes nicht gefundene Feld kommt zurueck und wird am Panel angezeigt.
 *
 * Dieses Modul ist rein (kein DOM, kein fs): Server (rezeptFehler) und Client teilen es.
 */

export interface AusfuellFeld { selektor: string; wert?: string; haken?: boolean }
export interface AusfuellRezept { profil: string; felder: AusfuellFeld[] }
export type ProfilWerte = Record<string, string>;

export const MAX_FELDER = 40;
const PROFILNAME = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const PLATZHALTER = /\{([a-z_]+)\}/g;

/** Felder, die aus dem Profil abgeleitet werden — neben denen, die direkt darin stehen. */
export const ABGELEITETE_FELDER = ['name', 'geburtsdatum_iso', 'geburtstag', 'geburtsmonat', 'geburtsjahr'] as const;

/**
 * Prueft die Form von `inhalt.ausfuellen`. Liefert den ERSTEN Fehler als Satz oder null.
 * `bekannteProfile` (wenn gegeben): Profilname muss darin stehen.
 * `profilFelder` (wenn gegeben): jeder Platzhalter muss darin oder in ABGELEITETE_FELDER stehen.
 */
export function ausfuellFehler(x: unknown, bekannteProfile?: readonly string[], profilFelder?: readonly string[]): string | null {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return 'ausfuellen muss ein Objekt { profil, felder } sein';
  const r = x as Record<string, unknown>;
  const erlaubt = new Set(['profil', 'felder']);
  const fremd = Object.keys(r).filter(k => !erlaubt.has(k));
  if (fremd.length) return `ausfuellen kennt nur profil und felder (unbekannt: ${fremd.join(', ')})`;
  if (typeof r.profil !== 'string' || !PROFILNAME.test(r.profil)) return 'ausfuellen.profil muss ein Profilname sein (klein, z. B. "rafael")';
  if (bekannteProfile && !bekannteProfile.includes(r.profil)) {
    return `ausfuellen.profil "${r.profil}" gibt es nicht (bekannt: ${bekannteProfile.join(', ') || 'keine'})`;
  }
  if (!Array.isArray(r.felder) || !r.felder.length) return 'ausfuellen.felder muss eine nicht-leere Liste sein';
  if (r.felder.length > MAX_FELDER) return `ausfuellen.felder: hoechstens ${MAX_FELDER} Felder`;
  for (const [i, f] of r.felder.entries()) {
    const wo = `ausfuellen.felder[${i}]`;
    if (!f || typeof f !== 'object' || Array.isArray(f)) return `${wo} muss ein Objekt sein`;
    const o = f as Record<string, unknown>;
    const unbekannt = Object.keys(o).filter(k => !['selektor', 'wert', 'haken'].includes(k));
    if (unbekannt.length) return `${wo} kennt nur selektor, wert, haken (unbekannt: ${unbekannt.join(', ')})`;
    if (typeof o.selektor !== 'string' || !o.selektor.trim() || o.selektor.length > 300 || /[\r\n]/.test(o.selektor)) {
      return `${wo}.selektor muss ein CSS-Selektor sein (eine Zeile, hoechstens 300 Zeichen)`;
    }
    const hatWert = o.wert !== undefined, hatHaken = o.haken !== undefined;
    if (hatWert === hatHaken) return `${wo} braucht GENAU eins von wert (Text) oder haken (true)`;
    if (hatHaken && o.haken !== true) return `${wo}.haken darf nur true sein — einen Haken ENTFERNEN tut die Vorbefuellung nie`;
    if (hatWert) {
      if (typeof o.wert !== 'string' || !o.wert.length || o.wert.length > 500) return `${wo}.wert muss ein nicht-leerer Text sein (hoechstens 500 Zeichen)`;
      if (profilFelder) {
        const bekannt = new Set<string>([...profilFelder, ...ABGELEITETE_FELDER]);
        for (const [, name] of o.wert.matchAll(PLATZHALTER)) {
          if (!bekannt.has(name)) return `${wo}.wert: Platzhalter {${name}} steht nicht im Profil (bekannt: ${[...bekannt].join(', ')})`;
        }
      }
    }
  }
  return null;
}

/** Profil um abgeleitete Felder ergaenzen (Name, Geburtsdatum in Teilen und als ISO fuer <input type=date>). */
export function profilWerte(roh: ProfilWerte): ProfilWerte {
  const w: ProfilWerte = { ...roh };
  if (w.vorname && w.nachname && !w.name) w.name = `${w.vorname} ${w.nachname}`;
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(w.geburtsdatum ?? '');
  if (m) {
    const [, t, mo, j] = m;
    w.geburtstag ??= t.padStart(2, '0'); w.geburtsmonat ??= mo.padStart(2, '0'); w.geburtsjahr ??= j;
    w.geburtsdatum_iso ??= `${j}-${mo.padStart(2, '0')}-${t.padStart(2, '0')}`;
  }
  return w;
}

/** `{feld}` aus dem Profil ersetzen. Fehlende Felder werden gemeldet, NICHT leer eingesetzt. */
export function ersetzePlatzhalter(wert: string, profil: ProfilWerte): { text: string; fehlt: string[] } {
  const fehlt: string[] = [];
  const text = wert.replace(PLATZHALTER, (ganz, name: string) => {
    const v = profil[name];
    if (v === undefined || v === '') { fehlt.push(name); return ganz; }
    return v;
  });
  return { text, fehlt };
}

export interface AufgeloestesFeld { selektor: string; wert?: string; haken?: true }

/** Rezept + Profil -> Felder mit echten Werten. Felder mit fehlendem Profilwert landen in `fehlt`. */
export function loeseAuf(rezept: AusfuellRezept, profil: ProfilWerte): { felder: AufgeloestesFeld[]; fehlt: Array<{ selektor: string; feld: string }> } {
  const werte = profilWerte(profil);
  const felder: AufgeloestesFeld[] = [];
  const fehlt: Array<{ selektor: string; feld: string }> = [];
  for (const f of rezept.felder) {
    if (f.haken) { felder.push({ selektor: f.selektor, haken: true }); continue; }
    const { text, fehlt: f2 } = ersetzePlatzhalter(f.wert ?? '', werte);
    if (f2.length) { for (const feld of f2) fehlt.push({ selektor: f.selektor, feld }); continue; }
    felder.push({ selektor: f.selektor, wert: text });
  }
  return { felder, fehlt };
}

export interface FuellErgebnis {
  gefuellt: string[];
  schon: string[];
  gehakt: string[];
  fehlt: string[];
  fremdeIframes: string[];
  fehler: string[];
}

/**
 * Das Skript, das IN der Seite laeuft (Funktionsrumpf, liefert ein JSON-String-Ergebnis).
 * Es sucht im Dokument und in allen same-origin-Iframes (rekursiv); cross-origin-Iframes
 * werden aufgezaehlt, damit das Panel sie als Grund fuer ein fehlendes Feld nennen kann.
 * Setzt Werte ueber den NATIVEN Setter + input/change (React/Vue sehen sonst nichts).
 * Ruft nie submit() und klickt nie etwas anderes als einen ungesetzten Haken.
 */
export function baueFuellSkript(felder: AufgeloestesFeld[]): string {
  return `
const FELDER = ${JSON.stringify(felder)};
const out = { gefuellt: [], schon: [], gehakt: [], fehlt: [], fremdeIframes: [], fehler: [] };
const docs = [];
const sammle = (d) => { docs.push(d); for (const f of d.querySelectorAll('iframe,frame')) {
  let inner = null; try { inner = f.contentDocument; } catch (e) { inner = null; }
  if (inner && inner.documentElement) sammle(inner); else out.fremdeIframes.push(f.src || '(ohne src)'); } };
sammle(document);
const finde = (sel) => { for (const d of docs) { let el = null; try { el = d.querySelector(sel); } catch (e) { out.fehler.push(sel + ': ungueltiger Selektor'); return null; } if (el) return el; } return undefined; };
for (const f of FELDER) {
  const el = finde(f.selektor);
  if (el === null) continue;
  if (!el) { out.fehlt.push(f.selektor); continue; }
  try {
    if (f.haken) {
      if (el.type !== 'checkbox' && el.type !== 'radio') { out.fehler.push(f.selektor + ': kein Haken (' + (el.type || el.tagName) + ')'); continue; }
      if (el.checked) { out.schon.push(f.selektor); continue; }
      el.click();
      if (!el.checked) { el.checked = true; el.dispatchEvent(new Event('input', { bubbles: true })); el.dispatchEvent(new Event('change', { bubbles: true })); }
      out.gehakt.push(f.selektor); continue;
    }
    if (!('value' in el)) { out.fehler.push(f.selektor + ': kein Eingabefeld (' + el.tagName + ')'); continue; }
    if (el.value === f.wert) { out.schon.push(f.selektor); continue; }
    // Leer = Textfeld ohne Inhalt bzw. Auswahl noch auf der ersten (Vorgabe-)Option. Alles andere hat ein Mensch gesetzt.
    if (el.tagName === 'SELECT' ? el.selectedIndex > 0 : el.value !== '') { out.schon.push(f.selektor); continue; }
    const proto = Object.getPrototypeOf(el);
    const desc = Object.getOwnPropertyDescriptor(proto, 'value');
    if (desc && desc.set) desc.set.call(el, f.wert); else el.value = f.wert;
    if (el.tagName === 'SELECT' && el.value !== f.wert) { out.fehler.push(f.selektor + ': Auswahl "' + f.wert + '" gibt es nicht'); continue; }
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    out.gefuellt.push(f.selektor);
  } catch (e) { out.fehler.push(f.selektor + ': ' + ((e && e.message) || e)); }
}
return JSON.stringify(out);`;
}

/** Ein Satz fuer die Statuszeile am Panel. `ok` = nichts fehlt, nichts schiefgegangen. */
export function fuellZusammenfassung(e: FuellErgebnis, profilFehlt: Array<{ selektor: string; feld: string }> = []): { ok: boolean; text: string } {
  const teile: string[] = [];
  const n = e.gefuellt.length + e.gehakt.length;
  if (n) teile.push(`${n} eingetragen`);
  if (e.schon.length) teile.push(`${e.schon.length} schon befüllt`);
  const probleme: string[] = [];
  if (e.fehlt.length) {
    probleme.push(`nicht gefunden: ${e.fehlt.join(', ')}`
      + (e.fremdeIframes.length ? ` — die Seite hat ${e.fremdeIframes.length} fremde Iframes (cross-origin, nicht erreichbar): ${e.fremdeIframes.slice(0, 2).join(', ')}` : ''));
  }
  if (e.fehler.length) probleme.push(e.fehler.join('; '));
  if (profilFehlt.length) probleme.push(`fehlt im Profil: ${profilFehlt.map(p => `{${p.feld}}`).join(', ')}`);
  return { ok: probleme.length === 0, text: [...teile, ...probleme].join(' · ') || 'nichts zu tun' };
}
